/**
 * Story 034 — bootstraps the per-user Lyse preferences on Analysis-page
 * mount. Mirrors `CataloguesBootstrap` (Story 033) in shape.
 *
 * Responsibilities:
 *   1. Fire `useLysePreferencesQuery` so the GET lands once.
 *   2. Apply server-side state to the live stores (image-state filter,
 *      catalogue store, workspace UI store) once the query resolves.
 *   3. Mount the `window.__aeroLysePrefs` test-bridge so the
 *      acceptance specs can assert hydration + drive updates.
 */
import { useEffect, useRef } from "react";
import {
  useLysePreferencesQuery,
  useUpdateLysePreferencesMutation,
  useResetLysePreferencesMutation,
  type LysePreferencesPatch,
} from "./queries.js";
import { factoryDefaults, type LysePreferences } from "./schema.js";
import { useImageStateFilterStore } from "../analysis/state/imageStateFilterStore.js";

interface TestBridge {
  prefs: LysePreferences;
  update: (patch: LysePreferencesPatch) => void;
  flush: () => void;
  reset: () => Promise<void>;
}

declare global {
  interface Window {
    __aeroLysePrefs?: TestBridge;
  }
}

/**
 * Mount once per Analysis page. Renders nothing.
 */
export function PreferencesBootstrap(): React.ReactElement {
  const query = useLysePreferencesQuery();
  const { update, flush } = useUpdateLysePreferencesMutation();
  const { reset } = useResetLysePreferencesMutation();

  // Apply server prefs to live stores. Only re-run when the loaded
  // snapshot changes (not on every render).
  const setImageStates = useImageStateFilterStore((s) => s.setStates);
  const lastAppliedRef = useRef<string>("");
  useEffect(() => {
    const next = JSON.stringify(query.prefs.imageStateFilter);
    if (next !== lastAppliedRef.current) {
      lastAppliedRef.current = next;
      // Default `['active']` is the same as the store's initial state,
      // so this is a no-op for the 404 case.
      setImageStates(query.prefs.imageStateFilter);
    }
  }, [query.prefs.imageStateFilter, setImageStates]);

  // Mount the test bridge. The `prefs` field is always present
  // (factory defaults during pending). Re-mount on every render so
  // the snapshot is fresh — cheap; no DOM cost.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const w = window;
    w.__aeroLysePrefs = {
      prefs: query.prefs,
      update,
      flush,
      reset,
    };
    return () => {
      if (w.__aeroLysePrefs?.prefs === query.prefs) {
        delete w.__aeroLysePrefs;
      }
    };
  }, [query.prefs, update, flush, reset]);

  return <></>;
}

/** Convenience for callers that need the factory shape outside React. */
export { factoryDefaults };
